import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./DateInput.css";

/**
 * Поле выбора даты с локализованной подсказкой формата.
 *
 * Проблема: браузер сам рисует подсказку/маску в <input type="date">
 * («дд.мм.гггг» или «mm/dd/yyyy») на языке ОС и полностью игнорирует
 * и атрибут lang, и атрибут placeholder. То есть при английском интерфейсе
 * на русской Windows Chrome показывал русскую маску.
 *
 * Решение: системный текст скрывается (color: transparent), а поверх
 * рисуется подпись из i18n (common.datePlaceholder). Подпись держится всё
 * время, пока поле пустое и пользователь не начал ввод, — в том числе
 * в фокусе (иначе при клике снова проступала русская маска браузера).
 * Как только пользователь начал набирать, системная маска показывается —
 * по ней удобнее вводить сегменты.
 *
 * Во всём остальном это обычный <input type="date">: значение в формате
 * yyyy-mm-dd, нативный календарь, все прочие props пробрасываются как есть.
 *
 * @param {string} [hint] — необязательный текст подсказки вместо
 *   common.datePlaceholder.
 */
function DateInput({ value, className = "", hint, onKeyDown, onBlur, onInput, ...rest }) {
  const { t } = useTranslation("common");
  const [typing, setTyping] = useState(false);

  const isEmpty = !value;
  const showHint = isEmpty && !typing;

  const wrapperClasses = ["date-field"];
  if (showHint) wrapperClasses.push("date-field--masked");
  // form-control растягивается на всю ширину — обёртка должна вести себя так же
  if (/\bform-control\b/.test(className)) wrapperClasses.push("date-field--block");

  const startTyping = () => {
    // bail-out: после первого нажатия состояние уже true, React пропускает
    // ре-рендер — контролируемое поле не мешает набору сегментов.
    setTyping((prev) => prev || true);
  };

  // Системную маску гасим ещё и инлайн-стилем: он бьёт любые CSS-правила,
  // независимо от порядка загрузки и специфичности. В CSS остаётся только
  // часть для внутренних псевдоэлементов (::-webkit-datetime-edit*),
  // которые через атрибут style не задать.
  const inputStyle = showHint
    ? {
        ...rest.style,
        color: "transparent",
        WebkitTextFillColor: "transparent",
        caretColor: "#212529",
      }
    : rest.style;

  return (
    <span className={wrapperClasses.join(" ")}>
      <input
        type="date"
        {...rest}
        value={value ?? ""}
        className={className}
        style={inputStyle}
        onKeyDown={(e) => {
          startTyping();
          onKeyDown?.(e);
        }}
        onInput={(e) => {
          startTyping();
          onInput?.(e);
        }}
        onBlur={(e) => {
          setTyping(false);
          onBlur?.(e);
        }}
      />
      {showHint && (
        <span className="date-field__hint" aria-hidden="true">
          {hint ?? t("datePlaceholder")}
        </span>
      )}
    </span>
  );
}

export default DateInput;
